const ISO_DATE = /^\d{4}-\d{2}-\d{2}T/;
const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** Notification params are stored raw (dates as ISO, numbers as numbers) so
 *  the history renders in whatever language the user reads it in. */
export function formatNotificationParams(
  params: Record<string, unknown> | undefined,
  lang: string
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value == null) {
      out[key] = '';
      continue;
    }
    if (typeof value === 'string' && ISO_DATE.test(value)) {
      out[key] = formatDateTime(value, lang);
      continue;
    }
    if (typeof value === 'string' && DAY_ONLY.test(value)) {
      out[key] = formatDay(value, lang);
      continue;
    }
    if (typeof value === 'number') {
      out[key] = new Intl.NumberFormat(lang).format(value);
      continue;
    }
    out[key] = String(value);
  }
  return out;
}

function formatDateTime(iso: string, lang: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(lang, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

const UNITS: Array<{ limit: number; seconds: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { limit: 60, seconds: 1, unit: 'second' },
  { limit: 3600, seconds: 60, unit: 'minute' },
  { limit: 86400, seconds: 3600, unit: 'hour' },
  { limit: 604800, seconds: 86400, unit: 'day' },
  { limit: 2629800, seconds: 604800, unit: 'week' },
  { limit: 31557600, seconds: 2629800, unit: 'month' },
];

export function relativeTime(iso: string, lang: string, now: Date = new Date()): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const diffSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const absolute = Math.abs(diffSeconds);
  const formatter = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });

  for (const { limit, seconds, unit } of UNITS) {
    if (absolute < limit) {
      return formatter.format(Math.round(diffSeconds / seconds), unit);
    }
  }
  return formatter.format(Math.round(diffSeconds / 31557600), 'year');
}

// A day carries no hour: read at UTC noon so no timezone can shift it to the day before.
function formatDay(day: string, lang: string): string {
  const date = new Date(`${day}T12:00:00Z`);
  if (Number.isNaN(date.getTime())) {
    return day;
  }
  return new Intl.DateTimeFormat(lang, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date);
}
